// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { concatStyleSets, ITextField, mergeStyles, Stack } from '@fluentui/react';
import { ChatMyMessage } from '@fluentui-contrib/react-chat';
import { mergeClasses } from '@fluentui/react-components';
import { useTheme } from '../../../theming/FluentThemeProvider';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { editBoxStyle, editingButtonStyle, editBoxStyleSet, inputBoxIcon } from '../../styles/EditBox.styles';
import { InputBoxComponent } from '../../InputBoxComponent';
import { InputBoxButton } from '../../InputBoxButton';
import { MessageThreadStrings } from '../../MessageThread';
import { useChatMyMessageStyles } from '../../styles/MessageThread.styles';
import { ChatMessage } from '../../../types';
import { chatMessageFailedTagStyle, editChatMessageFailedTagStyle, chatMessageFailedTagStackItemStyle, editChatMessageButtonsStackStyle, useChatMessageEditContainerStyles } from '../../styles/ChatMessageComponent.styles';
import { MAXIMUM_LENGTH_OF_MESSAGE } from '../../utils/SendBoxUtils';
import { getMessageState, onRenderCancelIcon, onRenderSubmitIcon, getTextValidationErrorMessage } from '../../utils/ChatMessageComponentAsEditBoxUtils';
/** @private */
export type ChatMessageComponentAsEditBoxProps = {
  onCancel?: (messageId: string) => void;
  onSubmit: (text: string) => void;
  message: ChatMessage;
  strings: MessageThreadStrings;
};

/**
 * @private
 */
export const ChatMessageComponentAsEditBox = (props: ChatMessageComponentAsEditBoxProps): JSX.Element => {
  const {
    onCancel,
    onSubmit,
    strings,
    message
  } = props;
  const [textValue, setTextValue] = useState<string>(message.content || '');
  const editTextFieldRef = React.useRef<ITextField>(null);
  const theme = useTheme();
  const messageState = getMessageState(textValue);
  const submitEnabled = messageState === 'OK';
  const editContainerStyles = useChatMessageEditContainerStyles();
  const chatMyMessageStyles = useChatMyMessageStyles();
  useEffect(() => {
    editTextFieldRef.current?.focus();
  }, []);
  const setText = (event?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string): void => {
    setTextValue(newValue ?? '');
  };
  const textValidationErrorMessage = useMemo(() => {
    return getTextValidationErrorMessage(messageState, strings.editBoxTextLimit, strings.editBoxEmptyWarningText);
  }, [messageState, strings.editBoxTextLimit, strings.editBoxEmptyWarningText]);
  const iconClassName = useCallback((isHover: boolean) => {
    const color = isHover ? theme.palette.accent : theme.palette.neutralSecondary;
    return mergeStyles(inputBoxIcon, {
      color
    });
  }, [theme.palette.accent, theme.palette.neutralSecondary]);
  const onRenderThemedCancelIcon = useCallback((isHover: boolean) => {
    return onRenderCancelIcon(iconClassName(isHover));
  }, [iconClassName]);
  const onRenderThemedSubmitIcon = useCallback((isHover: boolean) => {
    return onRenderSubmitIcon(iconClassName(isHover));
  }, [iconClassName]);
  const editBoxStyles = useMemo(() => {
    return concatStyleSets(editBoxStyleSet, {
      textField: {
        borderColor: theme.palette.themePrimary
      }
    });
  }, [theme.palette.themePrimary]);
  const getContent = (): JSX.Element => {
    return <>
        <InputBoxComponent data-ui-id="edit-box" textFieldRef={editTextFieldRef} inputClassName={editBoxStyle} placeholderText={strings.editBoxPlaceholderText} textValue={textValue} onChange={setText} onKeyDown={ev => {
        if (ev.key === 'ArrowUp' || ev.key === 'ArrowDown') {
          ev.stopPropagation();
        }
      }} onEnterKeyDown={() => {
        submitEnabled && onSubmit(textValue);
      }} supportNewline={false} maxLength={MAXIMUM_LENGTH_OF_MESSAGE} errorMessage={textValidationErrorMessage} styles={editBoxStyles}></InputBoxComponent>
        <Stack horizontal horizontalAlign="end" className={editChatMessageButtonsStackStyle} tokens={{
        childrenGap: '0.25rem'
      }}>
          {message.failureReason && <Stack.Item grow align="stretch" className={chatMessageFailedTagStackItemStyle}>
              <div className={mergeStyles(chatMessageFailedTagStyle(theme), editChatMessageFailedTagStyle)}>
                {message.failureReason}
              </div>
            </Stack.Item>}
          <Stack.Item align="end">
            <InputBoxButton className={editingButtonStyle} ariaLabel={strings.editBoxCancelButton} tooltipContent={strings.editBoxCancelButton} onRenderIcon={onRenderThemedCancelIcon} onClick={() => {
            onCancel && onCancel(message.messageId);
          }} id={'dismissIconWrapper'} data-testId="chat-message-edit-box-cancel-button" />
          </Stack.Item>
          <Stack.Item align="end">
            <InputBoxButton className={editingButtonStyle} ariaLabel={strings.editBoxSubmitButton} tooltipContent={strings.editBoxSubmitButton} onRenderIcon={onRenderThemedSubmitIcon} onClick={e => {
            // it's very important to pass an empty attachment here
            // so when user remvoes all attachments, UI can reflect it instantly
            // if you set it to undefined, the attachments pre-edited would still be there
            // until edit message event is received
            submitEnabled && onSubmit(textValue);
            e.stopPropagation();
          }} id={'submitIconWrapper'} data-testId="chat-message-edit-box-submit-button" />
          </Stack.Item>
        </Stack>
        {}
      </>;
  };
  const attached = message.attached === true ? 'center' : message.attached === 'bottom' ? 'bottom' : 'top';
  return <ChatMyMessage attached={attached} root={{
    className: mergeClasses(chatMyMessageStyles.root)
  }} body={{
    className: mergeClasses(editContainerStyles.body, message.failureReason !== undefined ? editContainerStyles.bodyError : editContainerStyles.bodyDefault, attached !== 'top' ? editContainerStyles.bodyAttached : undefined)
  }}>
      {getContent()}
    </ChatMyMessage>;
};